import { gql, useQuery } from '@sortlist-frontend/data-fetching/public';
import { useState } from 'react';

import { acceptLanguageHeader } from '_core/api/api-nextjs-proxy';

export const REVIEWS_PER_PAGE = 100;

export const AGENCY_REVIEWS_QUERY = gql(`
  query GetAgencyReviews($slug: String!, $first: Int!, $after: String) {
    agency(slug: $slug) {
      id
      reviews(first: $first, after: $after) {
        totalCount
        averageRating
        pageInfo {
          endCursor
          hasNextPage
        }
        nodes {
          id
          slug
          rating
          userFullName
          userCompanyName
          userPictureUrl
          userJobTitle
          ratingBudget
          ratingCollaboration
          ratingQuality
          ratingSchedule
          descriptionSummary
          descriptionCons
          descriptionPros
          recommended
          reply
          companySize
          sector {
            id
            name
          }
          service {
            id
            expertise {
              id
              name
            }
          }
          verifiedAt
          updatedAt
          createdAt
        }
      }
    }
  }
`);

export const useGetAgencyReviews = ({
  agencySlug,
  locale,
  perPage = REVIEWS_PER_PAGE,
}: {
  agencySlug: string;
  locale: string;
  perPage?: number;
}) => {
  const [isFetchingMore, setIsFetchingMore] = useState(false);

  const { data, fetchMore, loading, ...rest } = useQuery(AGENCY_REVIEWS_QUERY, {
    variables: { slug: agencySlug, first: perPage },
    context: {
      headers: {
        'Accept-Language': acceptLanguageHeader(locale),
      },
    },
  });

  const reviews = data?.agency?.reviews;
  const hasNextPage = reviews?.pageInfo.hasNextPage ?? false;

  const fetchMoreReviews = async () => {
    const endCursor = reviews?.pageInfo.endCursor;
    if (endCursor == null || isFetchingMore) return;

    setIsFetchingMore(true);
    try {
      await fetchMore({
        variables: { after: endCursor },
        updateQuery: (previousResult, { fetchMoreResult }) => {
          if (fetchMoreResult?.agency?.reviews == null) return previousResult;

          return {
            ...previousResult,
            agency: {
              ...previousResult.agency,
              reviews: {
                ...fetchMoreResult.agency.reviews,
                nodes: [
                  ...(previousResult.agency?.reviews?.nodes ?? []),
                  ...(fetchMoreResult.agency.reviews.nodes ?? []),
                ],
              },
            },
          };
        },
      });
    } finally {
      setIsFetchingMore(false);
    }
  };

  return {
    reviews,
    isLoading: loading,
    isFetchingMore,
    hasNextPage,
    fetchMoreReviews,
    ...rest,
  };
};
